import React, { useEffect, useRef } from 'react';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { Button } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';

// components
import HeaderNew from '../header-new/HeaderNew';
import {
  AppQRCode,
  handleStoreButtonClick,
} from 'src/components/app-qr-code/AppQRCode';

// styles and assets
import colors from 'src/styles/equalution/colors';
import SuccessImage from 'src/assets/images/success-page.png';
import SuccessImage2 from 'src/assets/images/success_mobile.png';
import PlaystoreLogo from 'src/assets/images/playstore-logo.png';
import AppstoreLogo from 'src/assets/images/appstore-logo.png';
import logo from 'src/assets/images/logo-new.svg';
import crossIcon from 'src/assets/icons/cross.svg';

// utils and redux
import { IAppState } from 'src/store';
import history from 'src/lib/history';
import { Coupon } from '../deprecated/payment/types';
import {
  setChallengePlan,
  setIsFitherProjectUser,
  setNoPaymentIntentAvailable,
  setSelectedPlanId,
  setIsBypassPlanSelection,
} from 'src/store/signup';
import { getEcommerceDataProperties, useViewport } from 'src/lib/utils';
import { track, EVENTS, trackSignupFunnel } from 'src/util/track';
import { useAppSelector } from 'src/store/hooks';
import { optimizely } from 'src/setupOptimizely';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { useChallengeProducts } from 'src/pages/subscription-new/hooks/useChallengeProducts';
import { useScrollToTop } from 'src/hooks/useScrollToTop';
import { useCampaignRedemption } from 'src/hooks/useCampaignRedemption';
import { clearReactivationState } from 'src/store/reactivation';
import { checkAuthentication } from 'src/lib/auth/actions';

const title = `Congratulations! You're one step closer to your best self!`;
const subTitle = `Download our App to start your transformation now.`;

declare global {
  interface Window {
    ire: any;
  }
}

const SuccessPage = () => {
  const { isMobile } = useViewport();
  const dispatch = useDispatch();

  useScrollToTop();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isChallengePackageFlow = searchParams.get('isChallengeFlow') === 'true';
  const isNutritionCoachingAdded =
    searchParams.get('nutritionCoachingAdded') === 'true';

  const dateTime = new Date().toISOString();

  // Redux Selectors
  const user = useSelector((state: IAppState) => state.auth?.user);
  const { selectedPlanId, allPlansSorted } = useAppSelector(
    state => state.signUp
  );
  const { challengeStartDate } = useChallengeProducts();
  const locationState = (history.location as any).state;
  const priceId: string = locationState?.priceId || '';
  const totalPrice = locationState?.totalPrice;
  const coupon: Coupon = locationState?.coupon;
  const hasTrackedConfirmationViewRef = useRef(false);

  useCampaignRedemption(priceId);

  useEffect(() => {
    dispatch(clearReactivationState());
    dispatch(checkAuthentication());
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      if (!user || !priceId || !totalPrice) {
        return;
      }
      const orderId = `${priceId}_${user?.id}_${dateTime}`;
      const trackProperties =
        getEcommerceDataProperties(
          selectedPlanId,
          allPlansSorted,
          {
            transaction_id: orderId,
            coupon_id: coupon?.id,
            coupon_name: coupon?.name,
          },
          1,
          user
        ) ?? {};

      track(EVENTS.purchase, { ecommerce: null });
      track(EVENTS.purchase, trackProperties);

      try {
        window.ire(
          'trackConversion',
          33152,
          {
            orderId,
            customerId: user?.id,
            customerEmail: user?.email,
            customerStatus: user?.status,
            currencyCode: 'AUD',
            orderPromoCode: coupon?.id || '',
            orderDiscount:
              ((coupon?.amountOff || 0) / 100).toFixed(2) || '0.00',
            items: [
              {
                sku: priceId,
                name: priceId,
                category: '',
                quantity: 1,
                subTotal: totalPrice || '0.00',
              },
            ],
          },
          { verifySiteDefinitionMatch: true }
        );
      } catch (e) {
        console.warn('[SuccessPage] window.ire tracking failed:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasTrackedConfirmationViewRef.current) {
      return;
    }

    trackSignupFunnel(EVENTS.signupConfirmationViewed, {
      selected_plan_id: priceId || undefined,
      total_price: totalPrice,
      coupon_id: coupon?.id,
      is_challenge_package_flow: Boolean(isChallengePackageFlow),
      has_nutrition_coaching: Boolean(isNutritionCoachingAdded),
    });
    hasTrackedConfirmationViewRef.current = true;
  }, [
    coupon?.id,
    isChallengePackageFlow,
    isNutritionCoachingAdded,
    priceId,
    totalPrice,
  ]);

  // cleanup redux states
  useEffect(() => {
    dispatch(setNoPaymentIntentAvailable(false));
    dispatch(setIsFitherProjectUser(false));
    dispatch(setChallengePlan(null));
    dispatch(setSelectedPlanId(''));
    dispatch(setIsBypassPlanSelection(false));
  }, [dispatch]);

  const TitleSection = () => {
    const titleStyles = {
      fontWeight: 700,
      textAlign: isMobile ? 'center' : 'left',
      fontSize: isChallengePackageFlow
        ? isMobile
          ? '20px'
          : '48px'
        : isMobile
        ? '22px'
        : '36px',
      lineHeight: isChallengePackageFlow
        ? isMobile
          ? '20px'
          : '56px'
        : isMobile
        ? '24px'
        : '36px',
    };

    const subtitle = isChallengePackageFlow ? (
      <Box
        fontWeight={700}
        lineHeight={isMobile ? '22px' : '34px'}
        fontSize={isMobile ? '15px' : '26px'}
        textAlign={isMobile ? 'center' : 'left'}>
        Download the equ app to start your transformation.
      </Box>
    ) : (
      <Box
        fontWeight={isMobile ? 700 : 600}
        fontSize={isMobile ? '18px' : '24px'}
        textAlign="center">
        {subTitle}
      </Box>
    );

    const challengeTitle = `Your transformation begins ${
      challengeStartDate ? moment(challengeStartDate).format('MMMM D') : ''
    }.`;

    return (
      <Stack gap={isMobile ? '18px' : '24px'}>
        {isNutritionCoachingAdded && (
          <Stack alignItems="center" sx={{ width: '100%' }}>
            <Box
              sx={{
                width: isMobile ? 50 : 65,
                height: isMobile ? 50 : 65,
                borderRadius: '50%',
                backgroundColor: colors.SALAD,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <CheckIcon sx={{ color: colors.WHITE, fontSize: 45 }} />
            </Box>
            <Typography
              sx={{
                fontSize: isMobile ? '20px' : '28px',
                fontWeight: 700,
                color: colors.SALAD,
                textAlign: 'center',
                mt: 1.5,
              }}>
              Coaching added
            </Typography>
          </Stack>
        )}
        <Box sx={titleStyles}>
          {isChallengePackageFlow ? challengeTitle : title}
        </Box>
        {subtitle}
      </Stack>
    );
  };

  const handleButtonClick = () => {
    const url = window.location.origin;

    window.location.href = url;
  };

  useEffect(() => {
    optimizely.track('success_page_view'); // event to track for quiz flow conversion
  }, []);

  return isMobile ? (
    <Stack
      height={'100vh'}
      alignItems={'center'}
      sx={{
        background: colors.OFF_WHITE,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}>
      <Box
        position={'absolute'}
        bottom={'-90px'}
        sx={{
          width: '100%',
          background: `url(${SuccessImage2})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '400px',
          zIndex: 1,
        }}
      />
      <Stack
        zIndex={10}
        width={'90%'}
        mt={'12px'}
        marginX={'16px'}
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}>
        <a href="https://joinequ.com/" target="_blank" rel="noreferrer">
          <img src={logo} alt="logo" style={{ height: 32, paddingTop: 10 }} />
        </a>
        <ButtonBase
          onClick={handleButtonClick}
          sx={{ height: '40px', width: '40px' }}>
          <img src={crossIcon} alt="cross" />
        </ButtonBase>
      </Stack>
      <Stack
        sx={{
          zIndex: 2,
          position: 'relative',
          width: '100%',
          alignItems: 'center',
        }}>
        <Stack paddingX={'24px'} mt={'10px'} gap={'24px'} alignItems={'center'}>
          <TitleSection />

          <Stack gap={'24px'} mt={'30px'} alignItems={'center'}>
            <ButtonBase
              disableRipple
              onClick={() => handleStoreButtonClick('appstore')}>
              <img src={AppstoreLogo} alt="appstore" height={'50px'} />
            </ButtonBase>
            <ButtonBase
              disableRipple
              onClick={() => handleStoreButtonClick('playstore')}>
              <img src={PlaystoreLogo} alt="playstore" height={'50px'} />
            </ButtonBase>
            <Stack alignItems={'center'} mb={3}>
              <Button
                size="sm"
                style={{
                  width: '200px',
                  marginTop: '30px',
                  position: 'relative',
                }}
                onClick={handleButtonClick}>
                Back to homepage
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  ) : (
    <Stack direction={'row'} minHeight={'100vh'}>
      <Stack
        flex={1}
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          background: `url(${SuccessImage})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}></Stack>
      <Stack
        flex={1}
        zIndex={10}
        sx={{ background: colors.CREAM_HALF, display: 'flex', flex: 1 }}>
        <Box ml={'32px'}>
          <HeaderNew module="login" />
        </Box>
        <Stack width={'530px'} paddingLeft={'80px'} gap={'32px'} mt={'40px'}>
          <TitleSection />
          <Stack>
            <AppQRCode />

            <Button
              block
              style={{
                width: '260px',
                position: 'relative',
                marginBlock: '30px',
              }}
              onClick={handleButtonClick}>
              Back to homepage
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SuccessPage;
